import { useTranslation } from 'react-i18next';
import { FolderKanban } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Button } from '@/shared/components/ui/button';
import { ProjectAccordionItem } from './ProjectAccordionItem';
import type { ProjectPerformanceEntry } from '../types';

interface ProjectAccordionProps {
  projects: ProjectPerformanceEntry[];
  periodStart: string;
  periodEnd: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

export function ProjectAccordion({
  projects,
  periodStart,
  periodEnd,
  isLoading = false,
  isError = false,
  onRetry,
}: ProjectAccordionProps) {
  const { t } = useTranslation('laborPerformance');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <FolderKanban className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">{t('projects.sectionTitle')}</span>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">{t('projects.error')}</p>
          {onRetry && (
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              {t('states.retry')}
            </Button>
          )}
        </div>
      )}

      {!isLoading && !isError && projects.length === 0 && (
        <p
          data-testid="labor-performance-project-accordion-empty"
          className="py-4 text-center text-sm text-muted-foreground"
        >
          {t('projects.empty')}
        </p>
      )}

      {!isLoading && !isError && projects.length > 0 && (
        <div data-testid="labor-performance-project-accordion" className="flex flex-col gap-2">
          {projects.map((project) => (
            <ProjectAccordionItem
              key={project.projectId}
              project={project}
              periodStart={periodStart}
              periodEnd={periodEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
}
